"use client";

// ── Uphacks after the round (`EX-HCK01`, `docs/29` §6.24, ADR-0182) ─────────
// A signed-in reader of a finished, published round sends one input against an
// accepted program they can read. The judge checks it in the band below
// practice (the validator, the setter's main solution, then the program), so
// the reader's list polls while anything of theirs is still being checked.
// Drawn on the canvas board `UphackSend`.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fetcher, type ApiEnvelope } from "@/lib/api";
import type { JudgeVerdict } from "@/features/quiz/coding-task-api";

/** The largest input the server takes: 1 MB, the judge's stream. */
export const UPHACK_INPUT_MAX_BYTES = 1_048_576;

export type UphackStatus =
  | "queued"
  | "running"
  | "refused"
  | "duplicate"
  | "main_failed"
  | "not_broken"
  | "pending"
  | "approved"
  | "rejected"
  | "errored";

export type OwnUphack = {
  hackId: string;
  letter: string;
  targetSubmissionId: string;
  target: { name: string; username: string | null } | null;
  status: UphackStatus;
  message: string | null;
  targetVerdict: JudgeVerdict | null;
  duplicateOf: number | null;
  testIdx: number | null;
  note: string | null;
  inputBytes: number;
  inputHead: string;
  createdAt: string;
  decidedAt: string | null;
};

export type MyUphacks = { items: OwnUphack[]; left: number; limit: number; open: boolean };

function unwrap<T>(res: ApiEnvelope<T>): T {
  if (!res.success) throw new Error(res.error.message);
  return res.data;
}

const slugPath = (slug: string) => `/v1/contests/${encodeURIComponent(slug)}`;

export async function fetchMyUphacks(slug: string): Promise<MyUphacks> {
  return unwrap(await fetcher<MyUphacks>(`${slugPath(slug)}/uphacks/mine`));
}

export async function sendUphack(slug: string, body: { submissionId: string; input: string }): Promise<{ hack: OwnUphack; left: number }> {
  return unwrap(
    await fetcher<{ hack: OwnUphack; left: number }>(`${slugPath(slug)}/uphacks`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  );
}

export const uphackKeys = { mine: (slug: string) => ["contest", slug, "uphacks", "mine"] as const };

/** Still with the judge: the list polls while one of these is on it. */
export function isChecking(status: UphackStatus): boolean {
  return status === "queued" || status === "running";
}

export function useMyUphacks(slug: string, enabled = true) {
  return useQuery({
    queryKey: uphackKeys.mine(slug),
    queryFn: () => fetchMyUphacks(slug),
    enabled: enabled && !!slug,
    retry: false,
    refetchInterval: (query) => (query.state.data?.items.some((item) => isChecking(item.status)) ? 5_000 : false),
  });
}

export function useSendUphack(slug: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { submissionId: string; input: string }) => sendUphack(slug, body),
    onSuccess: () => client.invalidateQueries({ queryKey: uphackKeys.mine(slug) }),
  });
}

/** The size the server will measure: UTF-8 bytes after Windows line ends are made plain. */
export function uphackInputBytes(input: string): number {
  return new TextEncoder().encode(input.replace(/\r\n?/g, "\n")).byteLength;
}

/** "84 B", "12 KB", "1.4 MB": a size a person reads at a glance, in the page's digits. */
export function formatBytes(bytes: number, locale: string): string {
  const number = (value: number, digits = 0) =>
    new Intl.NumberFormat(locale === "bn" ? "bn-BD" : "en-US", { maximumFractionDigits: digits }).format(value);
  if (bytes < 1024) return `${number(bytes)} B`;
  if (bytes < 1024 * 1024) return `${number(bytes / 1024)} KB`;
  return `${number(bytes / (1024 * 1024), 1)} MB`;
}

export type UphackTone = "busy" | "good" | "wait" | "bad" | "plain";

/** How a status looks: the chip's colour family. */
export function uphackTone(status: UphackStatus): UphackTone {
  switch (status) {
    case "queued":
    case "running":
      return "busy";
    case "approved":
      return "good";
    case "pending":
      return "wait";
    case "refused":
    case "main_failed":
    case "errored":
      return "bad";
    default:
      return "plain";
  }
}

export const TONE_CLASS: Record<UphackTone, string> = {
  busy: "bg-indigo-50 text-indigo-700",
  good: "bg-emerald-50 text-emerald-700",
  wait: "bg-amber-50 text-amber-800",
  bad: "bg-red-50 text-red-700",
  plain: "bg-neutral-100 text-neutral-600",
};

/** The error codes a send can meet, read into the sheet's own words. */
export type UphackSendRefusal =
  | "daily_limit"
  | "already_sent"
  | "too_large"
  | "not_accepted"
  | "round_not_final"
  | "own_problem"
  | "closed"
  | "input_invalid"
  | "generic";

export function uphackRefusalOf(error: unknown): UphackSendRefusal {
  const code = (error as { code?: string } | null)?.code;
  const reason = ((error as { details?: { reason?: string } } | null)?.details ?? {}).reason;
  if (code === "JUDGE_UPHACK_DAILY_LIMIT") return "daily_limit";
  if (code === "JUDGE_UPHACK_ALREADY_SENT") return "already_sent";
  if (code === "JUDGE_UPHACK_TOO_LARGE") return "too_large";
  if (code === "JUDGE_UPHACK_TARGET_NOT_ACCEPTED") return "not_accepted";
  // `EX-FNC06`: a function problem's hack that does not read against the signature.
  if (code === "JUDGE_FUNCTION_INPUT_INVALID") return "input_invalid";
  if (code === "JUDGE_UPHACK_NOT_OPEN") {
    if (reason === "round_not_final") return "round_not_final";
    if (reason === "own_problem") return "own_problem";
    return "closed";
  }
  return "generic";
}
